import { useParams } from "react-router-dom"
import { useActivities } from "@hydrologiq/shared/hooks/activities"
import * as gqlTypes from "@hydrologiq/gql-experience/graphql"
import { VStack, Text, Box, HStack } from "@chakra-ui/react"
import { useMemo, useCallback } from "react"
import { ActivitiesTableAdvanced } from "@hydrologiq/shared/components/activities"
import ActivityCreate from "@hydrologiq/shared/components/activities/ActivityCreate"
import { useAsset } from "../hooks/useAsset"

const ACTIVITY_KINDS = [gqlTypes.ActivityKind.Maintenance, gqlTypes.ActivityKind.Inspection]

const AssetMaintenance = () => {
  const { assetId = "" } = useParams()
  const { asset } = useAsset(assetId)
  const initialFilters = useMemo(
    () => ({
      asset: assetId,
      kind: ACTIVITY_KINDS.join(","),
    }),
    [assetId]
  )

  const {
    activities,
    pagination,
    loading,
    refreshing,
    error,
    filters,
    sort,
    filterOptions,
    setFilters,
    setSort,
    refetch,
    fetchActivities,
  } = useActivities(20, false, initialFilters)

  // Filter activities to only show maintenance kind (double-check)
  const maintenanceActivities = useMemo(() => {
    return activities.filter((activity) => {
      // Use ActivityKind enum if available, otherwise string comparison
      if (ACTIVITY_KINDS.includes(activity.kind as gqlTypes.ActivityKind)) {
        return true
      }
      return false
    })
  }, [activities])

  const handleActivityChanged = useCallback(() => {
    refetch()
  }, [refetch])

  // Handle pagination
  const handlePageChange = useCallback(
    (page: number) => {
      fetchActivities(page, pagination?.per_page || 20, filters, sort)
    },
    [fetchActivities, filters, sort, pagination?.per_page]
  )

  const handlePageSizeChange = useCallback(
    (pageSize: number) => {
      fetchActivities(1, pageSize, filters, sort)
    },
    [fetchActivities, filters, sort]
  )

  const headerActions = useMemo(
    () => (
      <HStack>
        <ActivityCreate
          activities={maintenanceActivities as any}
          asset={asset as any}
          activityKinds={ACTIVITY_KINDS as any}
        />
      </HStack>
    ),
    [maintenanceActivities, asset]
  )

  return (
    <VStack gap={4} align="stretch" width="100%">
      {error && (
        <Box p={4} bg="red.50" borderRadius="md">
          <Text color="red.600">Error loading activities: {error}</Text>
        </Box>
      )}
      <ActivitiesTableAdvanced
        asset={asset as any}
        activities={maintenanceActivities as any}
        loading={loading}
        refreshing={refreshing}
        error={error}
        onRetry={refetch}
        headerActions={headerActions}
        hideColumns={["asset", "fulfillment"]}
        serverPagination={
          pagination
            ? {
                pagination,
                onPageChange: handlePageChange,
                onPageSizeChange: handlePageSizeChange,
              }
            : undefined
        }
        serverFiltering={{
          filters,
          sort,
          onFiltersChange: setFilters,
          onSortChange: setSort,
          onClearAll: () => {
            setFilters({})
            setSort(null)
          },
        }}
        filterOptions={filterOptions}
        onActivityChanged={handleActivityChanged}
      />
    </VStack>
  )
}

export default AssetMaintenance
